import React, { useState, useEffect } from 'react';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import Header from './components/Header';
import Sidebar from './components/Sidebar';
import Canvas from './components/Canvas';
import Gallery from './components/Gallery';
import Footer from './components/Footer';
import { ImageModel, GeneratedImage, Style, StyleCategory, PromptHistory, PromptPreset, Tag, ImageFolder } from './types';
import { fetchModels, generateImage } from './services/api';
import { saveAs } from 'file-saver';
import { getDefaultResolution } from './config/resolutions';

const App: React.FC = () => {
  // 状态管理
  const [activeTab, setActiveTab] = useState<'create' | 'gallery'>('create');
  const [models, setModels] = useState<ImageModel[]>([]);
  const [selectedModel, setSelectedModel] = useState<ImageModel | null>(null);
  const [prompt, setPrompt] = useState<string>('');
  const [negativePrompt, setNegativePrompt] = useState<string>(''); // 修改负面提示词的初始值
  const [aspectRatio, setAspectRatio] = useState<string>('1:1');
  // 默认使用超清画质
  const [quality, setQuality] = useState<string>('ultra');
  // 添加种子值状态
  const [seed, setSeed] = useState<string>('');
  // 添加生成数量状态
  const [count, setCount] = useState<number>(1);
  // 添加生成状态
  const [isGenerating, setIsGenerating] = useState<boolean>(false);
  // 添加预览图片状态
  const [previewImages, setPreviewImages] = useState<GeneratedImage[]>([]);
  // 添加画廊图片状态
  const [galleryImages, setGalleryImages] = useState<GeneratedImage[]>([]);
  // 添加分辨率状态
  const [resolution, setResolution] = useState<string>(getDefaultResolution('1:1').id);
  
  // 添加自定义尺寸相关状态
  const [useCustomSize, setUseCustomSize] = useState<boolean>(false);
  const [customWidth, setCustomWidth] = useState<number>(768);
  const [customHeight, setCustomHeight] = useState<number>(768);

  // 添加提示词历史记录状态
  const [promptHistory, setPromptHistory] = useState<PromptHistory[]>([]);
  
  // 添加标签相关状态
  const [tags, setTags] = useState<Tag[]>([]);
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  
  // 添加收藏夹相关状态
  const [folders, setFolders] = useState<ImageFolder[]>([]);
  const [selectedFolder, setSelectedFolder] = useState<string | null>(null);
  const [showFavorites, setShowFavorites] = useState<boolean>(false);

  // 添加风格样式相关状态
  const [styleCategories, setStyleCategories] = useState<StyleCategory[]>([]);
  const [selectedStyle, setSelectedStyle] = useState<Style | null>(null);
  
  // 添加预设提示词状态
  const [presets, setPresets] = useState<PromptPreset[]>([]);

  // 初始化风格样式数据
  const initializeStyles = () => {
    try {
      // 创建风格样式数据
      const styleCategories: StyleCategory[] = [
        {
          id: 'popular',
          name: '流行风格',
          description: '常见的流行艺术风格',
          styles: [
            { id: '3d', name: '3D 样式', category: 'popular', description: '三维立体渲染、真实光影、空间感强、细节丰富、立体质感、逼真效果' },
            { id: 'watercolor', name: '水彩画', category: 'popular', description: '透明色彩、流动晕染、柔和边缘、轻盈质感、水分渗透效果、自然色调' },
            { id: 'street-art', name: '街头艺术', category: 'popular', description: '涂鸦、鲜艳色彩、都市元素、自由奔放、社会评论、公共空间' },
            { id: 'digital-art', name: '数字艺术', category: 'popular', description: '电子绘画、现代感、色彩鲜明、技术感、数字处理、创新表现' },
            { id: 'art-nouveau', name: '新艺术运动', category: 'popular', description: '有机曲线、自然灵感、装饰性强、优雅、植物纹样、流动线条' },
            { id: 'pop-art', name: '波普艺术', category: 'popular', description: '大众文化、鲜艳色彩、重复图案、漫画风格、商业元素、讽刺意味' },
            { id: 'baroque', name: '巴洛克风格', category: 'popular', description: '戏剧性光影、华丽装饰、动感构图、情感强烈、宗教主题、豪华感' },
            { id: 'bauhaus', name: '包豪斯', category: 'popular', description: '几何形状、功能主义、简洁线条、原色使用、现代设计、理性构成' },
            { id: 'black-white', name: '黑白', category: 'popular', description: '强烈对比、无彩色、明暗表现、简约纯粹、情绪化、戏剧性' },
            { id: 'botero', name: '博特罗', category: 'popular', description: '夸张比例、圆润人物、膨胀形态、体积感人物、哥伦比亚艺术风格' },
            { id: 'cartoon', name: '卡通片', category: 'popular', description: '简化线条、夸张特征、明亮色彩、平面设计、幽默风格、活泼形象' },
            { id: 'comics', name: '漫画', category: 'popular', description: '分格叙事、对话框、动态线条、角色表现、连续性、故事性强' },
            { id: 'cubism', name: '立体主义', category: 'popular', description: '几何分解、多视角、平面化、分析性、碎片化形象、抽象倾向' },
            { id: 'pencil', name: '铅笔画', category: 'popular', description: '线条细腻、灰度变化、素描质感、手绘感强、精细刻画、明暗对比' },
            { id: 'disney', name: '迪斯尼', category: 'popular', description: '圆润角色、生动表情、梦幻色彩、童话风格、流畅动感、温馨氛围' },
            { id: 'ghibli', name: '吉卜力', category: 'popular', description: '细腻背景、自然元素、温暖色调、梦幻场景、人物简约、东方美学' },
            { id: 'expressionism', name: '表现主义', category: 'popular', description: '情感强烈、扭曲形态、主观表达、强烈色彩、笔触明显、内心世界' },
            { id: 'fantasy', name: '奇幻', category: 'popular', description: '魔法元素、异世界、神话生物、超自然景观、想象力丰富、史诗感' },
            { id: 'fauvism', name: '野兽派', category: 'popular', description: '强烈色彩、简化形式、表现力强、非自然色调、粗犷笔触、情感表达' },
            { id: 'photography', name: '摄影', category: 'popular', description: '真实捕捉、光影效果、瞬间凝固、构图精准、景深控制、写实风格' },
            { id: 'futurism', name: '未来主义', category: 'popular', description: '动态表现、速度感、科技元素、同时性、能量表达、前卫风格' },
            { id: 'gothic', name: '哥特式', category: 'popular', description: '尖拱结构、垂直线条、彩色玻璃、神秘氛围、宗教元素、黑暗风格' },
            { id: 'guohua', name: '国画', category: 'popular', description: '水墨、笔触、稀疏构图、留白、山水景观、竹子、花鸟、诗意氛围、优雅简约、竖轴格式、书法元素' },
            { id: 'hyperrealism', name: '超写实', category: 'popular', description: '极致细节、照片级精度、完美质感、逼真效果、技术精湛、现实增强' },
            { id: 'illustration', name: '插图', category: 'popular', description: '叙事性强、配合文本、风格多样、商业应用、信息传达、视觉解释' },
            { id: 'impressionism', name: '印象派', category: 'popular', description: '可见笔触、光影变化、户外场景、瞬间捕捉、色彩分离、日常主题' },
            { id: 'isometric', name: '等轴测', category: 'popular', description: '30度角投影、无透视、平行线条、几何精确、技术图示、游戏风格' },
            { id: 'minimalism', name: '极简主义', category: 'popular', description: '简约形式、有限元素、留白、几何形状、单色调、本质表达' },
            { id: 'neon', name: '霓虹灯', category: 'popular', description: '发光效果、鲜艳色彩、夜景氛围、都市风格、对比强烈、科技感' },
            { id: 'oil', name: '原油', category: 'popular', description: '厚重质感、丰富色彩、层次感强、光泽效果、混合技法、传统艺术' },
            { id: 'origami', name: '折纸', category: 'popular', description: '几何折痕、纸张质感、精确角度、简化形态、日式美学、立体构成' },
            { id: 'cake', name: '蛋糕', category: 'popular', description: '甜点造型、奶油质感、装饰细节、色彩鲜艳、层次结构、美食艺术' },
            { id: 'abstract', name: '抽象画', category: 'popular', description: '非具象、情感表达、形式探索、色彩实验、构成研究、主观诠释' },
            { id: 'pixar', name: '皮克斯', category: 'popular', description: '3D动画、精细质感、情感表达、角色鲜明、故事性强、温暖色调' },
            { id: 'pixel', name: '像素化', category: 'popular', description: '方块构成、低分辨率、复古游戏、有限色彩、清晰边缘、数字美学' },
            { id: 'plasticine', name: '橡皮泥', category: 'popular', description: '粘土质感、可塑性强、手工感、鲜艳色彩、立体造型、童趣风格' },
            { id: 'realistic', name: '现实', category: 'popular', description: '真实再现、准确比例、自然光影、细节丰富、客观表达、写实技法' },
            { id: 'renaissance', name: '文艺复兴', category: 'popular', description: '古典平衡、透视精准、解剖准确、明暗对比、宗教主题、人文精神' },
            { id: 'rococo', name: '洛可可', category: 'popular', description: '华丽装饰、柔和色彩、曲线优美、轻盈优雅、贵族风格、浪漫情调' },
            { id: 'romanticism', name: '浪漫主义', category: 'popular', description: '情感强烈、戏剧性场景、自然崇拜、个人表达、想象力丰富、神秘主义' },
            { id: 'surrealism', name: '超现实主义', category: 'popular', description: '梦境场景、意外组合、潜意识表达、违反逻辑、象征意义、心理探索' },
            { id: 'texture', name: '纹理', category: 'popular', description: '表面细节、触感强烈、材质表现、层次丰富、视觉触感、结构感' },
            { id: 'ukiyo-e', name: '浮世绘', category: 'popular', description: '日本木刻、平面色彩、精细线条、日常场景、浮世风情、东方美学' },
            { id: 'van-gogh', name: '梵高', category: 'popular', description: '强烈笔触、旋转线条、鲜明色彩、情感表达、星空旋涡、向日葵' },
            { id: 'vintage', name: '复古', category: 'popular', description: '怀旧氛围、老化效果、历史感、经典元素、时代特征、温暖色调' }
          ]
        },
        {
          id: 'artist',
          name: '艺术家风格',
          description: '著名艺术家的风格',
          styles: [
            { id: 'alvar-aalto', name: '阿尔托·阿尔瓦', category: 'artist', description: '芬兰现代主义建筑师和设计师，作品融合自然元素，线条流畅，注重功能性和人性化设计' },
            { id: 'ansel-adams', name: '亚当斯·尼尔', category: 'artist', description: '美国摄影大师，黑白风景摄影，精准曝光，高对比度，自然景观，壮丽山脉，精细细节' },
            { id: 'daniel-arsham', name: '阿尔轩 Daniel', category: 'artist', description: '当代艺术家，雕塑作品呈现侵蚀和结晶效果，未来考古学概念，单色调，石膏质感' },
            { id: 'francis-bacon', name: '培根·弗朗西斯', category: 'artist', description: '英国表现主义画家，扭曲人体，强烈情感表达，暗色调，孤独感，生存焦虑，空间压抑' },
            { id: 'alice-bailey', name: '拜利·爱丽丝', category: 'artist', description: '神秘主义作家和艺术家，精神性作品，宇宙能量，抽象符号，神秘色彩，内在智慧' },
            { id: 'lynda-barry', name: '巴里·琳达', category: 'artist', description: '美国漫画家，手绘风格，生动表情，日常生活，幽默讽刺，简笔画技法，强烈个人风格' },
            { id: 'santiago-calatrava', name: '卡拉特拉瓦圣地亚哥', category: 'artist', description: '西班牙建筑师，生物形态结构，白色建筑，动态设计，未来主义，桥梁和公共建筑' },
            { id: 'clyde-caldwell', name: '考德威尔·克莱德', category: 'artist', description: '美国奇幻艺术家，英雄人物，龙与魔法，鲜艳色彩，细节丰富，戏剧性光影，奇幻场景' },
            { id: 'caravaggio', name: '卡拉瓦乔', category: 'artist', description: '意大利巴洛克画家，强烈明暗对比，戏剧性光影，写实人物，宗教题材，情感强烈' },
            { id: 'cermisvieira', name: '塞尔明斯维亚', category: 'artist', description: '现代艺术家，抽象构图，鲜艳色彩，几何形式，动态感，情感表达，现代艺术风格' },
            { id: 'chiho-aoshima', name: '青岛千穗', category: 'artist', description: '日本当代艺术家，超现实风景，可爱与恐怖并存，鲜艳色彩，数字绘画，梦幻场景' },
            { id: 'claude-monet', name: '克劳德·莫奈', category: 'artist', description: '法国印象派大师，光影变化，户外写生，点彩技法，睡莲系列，自然景观，瞬间捕捉' },
            { id: 'paul-cezanne', name: '塞尚·保罗', category: 'artist', description: '法国后印象派画家，几何形式简化，色块构成，静物画，风景画，空间感探索' },
            { id: 'salvador-dali', name: '达利·萨尔瓦多', category: 'artist', description: '西班牙超现实主义大师，梦境场景，融化钟表，奇异组合，精细技法，象征意义' },
            { id: 'diego-rivera', name: '迭戈·里维拉', category: 'artist', description: '墨西哥壁画家，社会主题，政治内容，鲜艳色彩，墨西哥民族元素，工人形象' },
            { id: 'el-greco', name: '埃尔·格列柯', category: 'artist', description: '西班牙画家，拉长人物，精神性表达，强烈色彩，戏剧性构图，宗教主题' },
            { id: 'frida-kahlo', name: '弗里达·卡洛', category: 'artist', description: '墨西哥女画家，自画像，个人痛苦，墨西哥民族元素，鲜艳色彩，象征主义' },
            { id: 'paul-gauguin', name: '高更·保罗', category: 'artist', description: '法国后印象派画家，塔希提岛风景，原始主义，平面色彩，简化形式，异国情调' },
            { id: 'joan-miro', name: '胡安·米罗', category: 'artist', description: '西班牙超现实主义画家，抽象符号，原始形式，鲜艳色彩，星空元素，童趣风格' },
            { id: 'leonardo-da-vinci', name: '列奥纳多·达·芬奇', category: 'artist', description: '意大利文艺复兴全才，精细写实，解剖学准确，烟雾效果，精妙光影，神秘微笑' },
            { id: 'marc-chagall', name: '马克·夏加尔', category: 'artist', description: '俄裔法国现代艺术家，梦幻场景，飞行人物，民间故事，鲜艳色彩，犹太元素' },
            { id: 'michelangelo', name: '米开朗基罗·博纳罗蒂', category: 'artist', description: '意大利文艺复兴大师，雕塑感人物，强健身体，西斯廷天顶画，宗教场景，人体解剖' },
            { id: 'giorgio-morandi', name: '乔治·莫兰迪', category: 'artist', description: '意大利静物画家，简化形式，柔和色调，瓶罐构图，空间关系，宁静氛围' },
            { id: 'pablo-picasso', name: '巴勃罗·毕加索', category: 'artist', description: '西班牙现代艺术大师，立体主义创始人，多视角，几何分解，蓝色时期，创新风格' },
            { id: 'rembrandt', name: '伦勃朗', category: 'artist', description: '荷兰巴洛克画家，明暗对比，自画像，心理刻画，金色光芒，人性表达' },
            { id: 'siqueiros', name: '西凯罗斯', category: 'artist', description: '墨西哥壁画家，政治主题，动态构图，工业材料实验，强烈色彩，革命精神' },
            { id: 'xu-beihong', name: '徐悲鸿', category: 'artist', description: '中国现代绘画大师，擅长人物、动物特别是奔马，融合中西方技法，笔触有力，形象生动' },
            { id: 'zhang-daqian', name: '张大千', category: 'artist', description: '中国近现代国画大师，擅长山水、人物、花鸟，泼墨泼彩，气势恢宏，色彩艳丽' },
            { id: 'fu-baoshi', name: '傅抱石', category: 'artist', description: '中国现代国画大师，山水画风格独特，笔法苍劲有力，色彩浓重，意境深远' },
            { id: 'qi-baishi', name: '齐白石', category: 'artist', description: '中国现代国画大师，擅长花鸟虫鱼，风格朴实自然，笔法简练，构图独特，色彩鲜明' }
          ]
        },
        {
          id: 'special',
          name: '特殊样式',
          description: '特殊的艺术风格和主题',
          styles: [
            { id: 'cowboy', name: '牛仔风格', category: 'special', description: '西部荒野、牛仔服饰、荒漠景观、马匹、牧场、老式酒馆、美国西部风情' },
            { id: 'lego', name: '乐高', category: 'special', description: '积木结构、鲜艳色块、塑料质感、简化造型、拼插结构、童趣风格、像素化' },
            { id: 'halloween', name: '万圣节', category: 'special', description: '南瓜灯、幽灵、巫师、黑猫、蜘蛛网、橙黑配色、神秘恐怖氛围' },
            { id: 'horror', name: '恐怖', category: 'special', description: '黑暗场景、恐怖元素、血腥效果、超自然现象、惊悚氛围、扭曲形态' },
            { id: 'christmas', name: '圣诞节', category: 'special', description: '圣诞树、礼物、雪景、红绿配色、圣诞老人、温馨氛围、节日装饰' },
            { id: 'cyberpunk', name: '网络', category: 'special', description: '霓虹灯光、未来都市、高科技低生活、电子元素、赛博改造、雨夜场景' },
            { id: 'torn-paper', name: '撕裂的纸张', category: 'special', description: '纸张质感、撕裂边缘、拼贴效果、层次感、纹理细节、手工感' },
            { id: 'graffiti', name: '涂鸦', category: 'special', description: '街头艺术、喷漆效果、大胆色彩、都市元素、标签文字、反叛风格' },
            { id: 'matrix', name: '矩阵', category: 'special', description: '绿色代码、数字雨、黑客风格、虚拟现实、科技感、神秘氛围' },
            { id: 'holographic', name: '全息', category: 'special', description: '透明效果、彩虹光泽、光学折射、未来感、虚幻质感、光影变化' },
            { id: 'western', name: '西洋', category: 'special', description: '西方文化、欧式建筑、古典元素、西方历史场景、传统服饰' },
            { id: 'medieval', name: '中世纪', category: 'special', description: '城堡、骑士、盔甲、中世纪服饰、古老武器、宫廷场景、封建氛围' },
            { id: 'prehistoric', name: '史前史', category: 'special', description: '恐龙、原始人类、洞穴、原始森林、火山、史前生物、原始工具' },
            { id: 'ocean', name: '海洋', category: 'special', description: '深蓝色调、波浪纹理、海洋生物、水下光影、珊瑚礁、透明质感、流动感' },
            { id: 'jungle', name: '丛林', category: 'special', description: '茂密植被、深绿色调、热带雨林、野生动物、藤蔓缠绕、神秘氛围、自然野性' },
            { id: 'sci-fi', name: '科幻小说', category: 'special', description: '未来科技、太空元素、高科技设备、异星景观、全息投影、机械结构、科技感' },
            { id: 'low-poly', name: '低聚乙烯', category: 'special', description: '几何面、简化形状、棱角分明、三角形结构、简约风格、数字艺术感' },
            { id: 'voxel', name: '体素', category: 'special', description: '立方体像素、3D方块、游戏风格、体积元素、像素化、层次分明' },
            { id: 'space', name: '太空', category: 'special', description: '星空背景、宇宙尘埃、行星景观、太空飞船、失重感、深邃黑暗、星光闪烁' },
            { id: 'winter', name: '冬季', category: 'special', description: '白雪覆盖、冰晶效果、寒冷色调、冬季景观、温暖光源、节日氛围、宁静感' },
            { id: 'summer', name: '夏季', category: 'special', description: '明亮阳光、热带色彩、海滩元素、蓝天白云、鲜艳花卉、清凉水景、活力四射' },
            { id: 'autumn', name: '秋天', category: 'special', description: '金黄落叶、橙红色调、丰收景象、温暖光线、雾气朦胧、宁静氛围、自然转变' },
            { id: 'spring', name: '春天', category: 'special', description: '嫩绿新芽、樱花盛开、明亮色调、春雨细腻、生机勃勃、温暖阳光、清新感' },
            { id: 'steampunk', name: '蒸汽朋克', category: 'special', description: '蒸汽机械、铜质材料、齿轮元素、维多利亚风格、复古科技、工业革命、奇幻设定' },
            { id: 'noir', name: '黑色', category: 'special', description: '黑白对比、强烈明暗、犯罪元素、神秘氛围、城市夜景、雨夜场景、戏剧性光影' },
            { id: 'trash-art', name: '垃圾摇滚', category: 'special', description: '废弃物艺术、回收材料、朋克风格、粗糙质感、实验性、反传统' },
            { id: 'dark-academia', name: '黑暗学院', category: 'special', description: '古典学术氛围、深色调、复古书籍、哥特元素、神秘感、学院风格、文学艺术' }
          ]
        },
        {
          id: 'mixed',
          name: '混合样式',
          description: '多种风格的混合',
          styles: [
            { id: 'futurism-tribal', name: '未来主义 + 部落艺术', category: 'mixed', description: '未来科技与原始部落元素融合，高科技与原始图腾并存，动感与原始力量结合' },
            { id: 'minimalism-vintage', name: '极简主义 + 复古', category: 'mixed', description: '简约设计与复古元素结合，现代简洁与怀旧气息并存，干净线条与历史感交融' },
            { id: 'gothic-steampunk', name: '哥特式 + 蒸汽朋克', category: 'mixed', description: '中世纪哥特建筑与蒸汽时代机械融合，尖塔与齿轮并存，神秘与工业感结合' },
            { id: 'renaissance-fantasy', name: '文艺复兴 + 奇幻', category: 'mixed', description: '古典艺术与奇幻元素结合，文艺复兴精细技法与魔法世界交融，现实与幻想并存' },
            { id: 'futurism-art-nouveau-steampunk', name: '未来主义 + 新艺术风格 + 蒸汽朋克', category: 'mixed', description: '未来科技、有机曲线与蒸汽机械的三重融合，前卫、优雅与复古工业的混搭' },
            { id: 'minimalism-watercolor-surrealism', name: '极简主义 + 水彩画 + 超现实主义', category: 'mixed', description: '简约构图、水彩质感与超现实场景结合，简洁、流动与梦幻的混合' },
            { id: 'cubism-gothic-scifi', name: '立体主义 + 哥特式 + 科幻小说', category: 'mixed', description: '几何分解、中世纪氛围与未来科技的融合，多维视角、神秘感与高科技的混搭' },
            { id: 'vintage-cyberpunk-surrealism', name: '复古 + 赛博朋克 + 超现实主义', category: 'mixed', description: '怀旧元素、高科技霓虹与超现实场景的结合，复古感、未来感与梦境的交织' },
            { id: 'renaissance-abstract-fantasy', name: '文艺复兴 + 抽象 + 奇幻', category: 'mixed', description: '古典技法、抽象构成与奇幻元素的混合，传统与现代抽象及魔幻世界的融合' }
          ]
        }
      ];
      
      setStyleCategories(styleCategories);
    } catch (error) {
      console.error('初始化风格样式数据失败:', error);
    }
  };

  // 初始化加载模型、提示词历史和标签/收藏夹数据
  useEffect(() => {
    const loadModels = async () => {
      try {
        const availableModels = await fetchModels();
        setModels(availableModels);
        if (availableModels.length > 0) {
          setSelectedModel(availableModels[0]);
        }
        
        // 确保随机提示词库初始化
        
        // 加载随机提示词库 - 按不同模型类别分组
        const randomPrompts: PromptPreset[] = [
          // 写实风格提示词 - 适合核心基础模型
          { id: '1', name: '梦幻风景', prompt: '梦幻般的风景, 高山, 湖泊, 瀑布, 晚霞, 高清细节, 艺术感, 4K超高清', style: 'landscape', category: '核心基础模型' },
          { id: '2', name: '科幻城市', prompt: '未来科幻城市, 霓虹灯, 高楼大厦, 飞行器, 科技感, 细节丰富, 夜景, 光影效果', style: 'scifi', category: '核心基础模型' },
          { id: '3', name: '写实人像', prompt: '专业人像摄影, 自然光线, 浅景深, 精致五官, 完美肤质, 时尚服装, 工作室布景', style: 'portrait', category: '核心基础模型' },
          { id: '4', name: '自然风光', prompt: '壮丽自然风光, 日出, 山脉, 森林, 河流, 云海, 鸟瞰视角, 广角镜头, 高动态范围', style: 'nature', category: '核心基础模型' },
          { id: '5', name: '静物艺术', prompt: '精美静物摄影, 水果, 花卉, 玻璃器皿, 柔和光线, 质感表现, 专业布光, 高对比度', style: 'still-life', category: '核心基础模型' },
          { id: '21', name: '海洋世界', prompt: '深海场景, 珊瑚礁, 热带鱼群, 阳光穿透水面, 水下摄影, 蓝色渐变, 气泡效果, 高清质感', style: 'underwater', category: '核心基础模型' },
          { id: '22', name: '城市夜景', prompt: '城市夜景, 繁华都市, 车水马龙, 灯火通明, 摩天大楼, 反光效果, 长曝光, 都市生活', style: 'cityscape', category: '核心基础模型' },
          { id: '23', name: '雪山奇景', prompt: '壮丽雪山, 冰川, 极光, 星空, 雪松, 宁静湖泊, 冬季仙境, 震撼全景, 极致细节', style: 'winter', category: '核心基础模型' },
          { id: '24', name: '沙漠日落', prompt: '金色沙漠, 壮丽日落, 沙丘剪影, 仙人掌, 暖色调, 广袤无垠, 自然纹理, 宁静氛围', style: 'desert', category: '核心基础模型' },
          { id: '25', name: '建筑艺术', prompt: '宏伟建筑, 精细结构, 几何美学, 光影交错, 现代设计, 玻璃幕墙, 独特视角, 建筑摄影', style: 'architecture', category: '核心基础模型' },
          
          // 动漫风格提示词 - 适合动漫与二次元专用
          { id: '6', name: '动漫少女', prompt: '精美动漫少女, 细腻线条, 大眼睛, 精致服装, 飘逸长发, 校园场景, 日系插画风格', style: 'anime-girl', category: '动漫与二次元专用' },
          { id: '7', name: '动漫风景', prompt: '吉卜力风格动漫场景, 乡村, 自然, 细节丰富, 温暖阳光, 和平氛围, 梦幻色彩', style: 'anime-landscape', category: '动漫与二次元专用' },
          { id: '8', name: '赛博朋克', prompt: '赛博朋克动漫场景, 未来都市, 霓虹灯, 雨夜, 机械义肢, 高科技, 暗黑风格', style: 'cyberpunk-anime', category: '动漫与二次元专用' },
          { id: '9', name: '暗系动漫', prompt: '暗黑系动漫风格, 神秘氛围, 超自然元素, 恐怖场景, 怪物, 暗影, 紧张气氛', style: 'dark-anime', category: '动漫与二次元专用' },
          { id: '10', name: '动漫战斗', prompt: '动漫战斗场景, 激烈动作, 能量效果, 武器, 爆炸, 速度线, 强烈对比', style: 'anime-action', category: '动漫与二次元专用' },
          { id: '26', name: '魔法少女', prompt: '魔法少女动漫, 华丽变身, 闪亮特效, 魔法武器, 可爱造型, 梦幻背景, 战斗姿势, 彩虹色调', style: 'magical-girl', category: '动漫与二次元专用' },
          { id: '27', name: '机甲战士', prompt: '精密机甲, 未来战士, 机械细节, 金属质感, 驾驶舱视角, 战斗姿态, 科技武器, 动态构图', style: 'mecha', category: '动漫与二次元专用' },
          { id: '28', name: '幻想世界', prompt: '奇幻动漫场景, 浮空岛, 巨大树木, 水晶建筑, 魔法生物, 梦幻天空, 异世界风光, 细节丰富', style: 'fantasy-anime', category: '动漫与二次元专用' },
          { id: '29', name: '校园日常', prompt: '动漫校园场景, 樱花树下, 教室内部, 屋顶午餐, 放学时分, 夕阳余晖, 青春氛围, 日常生活', style: 'school-anime', category: '动漫与二次元专用' },
          { id: '30', name: '和风古风', prompt: '日本传统风格动漫, 和服角色, 古代建筑, 樱花飞舞, 竹林小径, 纸灯笼, 浮世绘风格, 细腻线条', style: 'japanese-anime', category: '动漫与二次元专用' },
          
          // 卡通风格提示词 - 适合欧美卡通与3D风格
          { id: '11', name: '迪士尼风格', prompt: '迪士尼风格角色, 可爱表情, 明亮色彩, 童话场景, 圆润线条, 温馨氛围', style: 'disney', category: '欧美卡通与3D风格' },
          { id: '12', name: '皮克斯3D', prompt: '皮克斯风格3D角色, 精细纹理, 逼真光影, 夸张表情, 温馨故事场景, 高品质渲染', style: 'pixar', category: '欧美卡通与3D风格' },
          { id: '13', name: '卡通动物', prompt: '可爱卡通动物角色, 拟人化, 富有表情, 明亮色彩, 简洁背景, 有趣姿势', style: 'cartoon-animal', category: '欧美卡通与3D风格' },
          { id: '14', name: '超级英雄', prompt: '卡通风格超级英雄, 夸张动作, 鲜艳服装, 城市背景, 战斗场景, 特效元素', style: 'superhero', category: '欧美卡通与3D风格' },
          { id: '15', name: '梦幻场景', prompt: 'Midjourney风格梦幻场景, 超现实元素, 奇幻色彩, 不可思议构图, 精细细节, 艺术感', style: 'midjourney', category: '欧美卡通与3D风格' },
          { id: '31', name: '蒸汽朋克', prompt: '蒸汽朋克卡通场景, 齿轮机械, 铜质装置, 维多利亚风格, 飞艇, 复古科技, 烟雾效果, 棕色调', style: 'steampunk', category: '欧美卡通与3D风格' },
          { id: '32', name: '太空冒险', prompt: '卡通太空冒险, 宇宙飞船, 外星球景观, 奇特生物, 星际探险, 科幻元素, 明亮星云, 未来科技', style: 'space-adventure', category: '欧美卡通与3D风格' },
          { id: '33', name: '童话世界', prompt: '童话风格插画, 魔法森林, 蘑菇屋, 小精灵, 奇幻生物, 彩虹桥, 梦幻色彩, 故事场景', style: 'fairytale', category: '欧美卡通与3D风格' },
          { id: '34', name: '游戏风格', prompt: '3D游戏风格场景, 奇幻角色, 魔法效果, 游戏UI元素, 战斗姿态, 宝箱道具, 任务场景, 鲜明色彩', style: 'game-style', category: '欧美卡通与3D风格' },
          { id: '35', name: '低多边形', prompt: '低多边形3D风格, 几何形状, 简化纹理, 鲜艳色块, 抽象场景, 现代感, 数字艺术, 简约设计', style: 'low-poly', category: '欧美卡通与3D风格' },
          
          // 艺术风格提示词 - 适合艺术与特殊效果
          { id: '16', name: '水彩艺术', prompt: '精美水彩画, 流动色彩, 透明效果, 纸张质感, 柔和边缘, 艺术构图, 情感表达', style: 'watercolor', category: '艺术与特殊效果' },
          { id: '17', name: '油画风格', prompt: '古典油画风格, 厚重笔触, 丰富色彩, 光影对比, 历史场景, 人物肖像, 细节丰富', style: 'oil-painting', category: '艺术与特殊效果' },
          { id: '18', name: '暗黑幻想', prompt: '暗黑幻想艺术, 神秘生物, 魔法元素, 哥特风格, 阴暗氛围, 超自然景观, 戏剧性光影', style: 'dark-fantasy', category: '艺术与特殊效果' },
          { id: '19', name: '抽象艺术', prompt: '现代抽象艺术, 几何形状, 大胆色彩, 非具象表达, 情感冲击, 艺术构成, 创意表现', style: 'abstract', category: '艺术与特殊效果' },
          { id: '20', name: '复古海报', prompt: '复古风格艺术海报, 20世纪设计, 鲜明色块, 简化形象, 排版元素, 做旧效果, 广告风格', style: 'vintage-poster', category: '艺术与特殊效果' },
          { id: '36', name: '印象派', prompt: '印象派艺术风格, 光影变化, 点彩技法, 户外场景, 自然光线, 柔和色调, 笔触可见, 氛围感强', style: 'impressionism', category: '艺术与特殊效果' },
          { id: '37', name: '波普艺术', prompt: '波普艺术风格, 流行文化元素, 大胆色彩, 重复图案, 漫画风格, 商业广告感, 现代都市, 鲜明对比', style: 'pop-art', category: '艺术与特殊效果' },
          { id: '38', name: '赛博朋克', prompt: '赛博朋克艺术, 霓虹灯效果, 未来都市, 数字雨, 全息投影, 机械改造人, 高科技与低生活, 反乌托邦', style: 'cyberpunk-art', category: '艺术与特殊效果' },
          { id: '39', name: '超现实主义', prompt: '超现实主义艺术, 梦境场景, 不可能构造, 悬浮物体, 错位空间, 象征意义, 心理暗示, 奇异组合', style: 'surrealism', category: '艺术与特殊效果' },
          { id: '40', name: '像素艺术', prompt: '复古像素艺术, 8位游戏风格, 方块构成, 有限调色板, 清晰边缘, 复古游戏场景, 简化形象, 怀旧感', style: 'pixel-art', category: '艺术与特殊效果' },
          
          // 新增类别：人物与角色 - 适合各种人物创作
          { id: '41', name: '科幻战士', prompt: '未来科幻战士, 高科技盔甲, 能量武器, 全息显示器, 未来战场, 机械义肢, 先进材质, 动态姿势', style: 'sci-fi-warrior', category: '人物与角色' },
          { id: '42', name: '奇幻角色', prompt: '奇幻角色肖像, 精灵特征, 魔法装备, 神秘氛围, 异世界背景, 华丽服饰, 魔法光效, 细节丰富', style: 'fantasy-character', category: '人物与角色' },
          { id: '43', name: '赛博格女孩', prompt: '赛博朋克女孩, 未来时尚, 霓虹灯反光, 机械植入物, 全息装饰, 都市背景, 科技配件, 冷色调', style: 'cyborg-girl', category: '人物与角色' },
          { id: '44', name: '古代武士', prompt: '日本武士肖像, 传统盔甲, 刀剑装备, 历史场景, 战斗姿态, 东方美学, 水墨风格, 强烈光影', style: 'samurai', category: '人物与角色' },
          { id: '45', name: '维京战士', prompt: '北欧维京战士, 皮革盔甲, 战斧武器, 龙船背景, 雪山环境, 部落纹身, 野性外观, 史诗感', style: 'viking', category: '人物与角色' },
          
          // 新增类别：风景与场景 - 适合环境创作
          { id: '46', name: '失落神庙', prompt: '古代失落神庙, 藤蔓缠绕, 破碎石柱, 神秘符文, 阳光透射, 尘埃粒子, 探险场景, 宏伟规模', style: 'lost-temple', category: '风景与场景' },
          { id: '47', name: '浮空城市', prompt: '天空中的浮空城市, 悬浮岛屿, 瀑布流下, 云海环绕, 飞行器, 未来建筑, 连接桥梁, 壮观景色', style: 'floating-city', category: '风景与场景' },
          { id: '48', name: '水晶洞穴', prompt: '神秘水晶洞穴, 发光晶体, 地下湖泊, 钟乳石, 反光效果, 蓝色调, 梦幻氛围, 探索场景', style: 'crystal-cave', category: '风景与场景' },
          { id: '49', name: '外星地貌', prompt: '外星球地貌, 奇异植被, 多重月亮, 异色天空, 不明生物, 奇怪岩石, 外星建筑, 科幻探索', style: 'alien-landscape', category: '风景与场景' },
          { id: '50', name: '末日废墟', prompt: '末日后废墟城市, 倒塌建筑, 生锈车辆, 荒凉街道, 野生植物入侵, 阴暗天空, 末日气氛, 生存场景', style: 'post-apocalyptic', category: '风景与场景' },
          
          // 新增提示词 - 更多风格和场景
          { id: '51', name: '霓虹赛博朋克', prompt: '霓虹赛博朋克城市, 雨夜, 反光地面, 高科技广告牌, 悬浮汽车, 全息投影, 拥挤街道, 未来感建筑', style: 'cyberpunk-neon', category: '风景与场景' },
          { id: '52', name: '古代神庙', prompt: '古代神庙遗迹, 巨大石柱, 神秘雕刻, 晨雾缭绕, 阳光穿透, 古老文明, 宏伟建筑, 历史感', style: 'ancient-temple', category: '风景与场景' },
          { id: '53', name: '水下城市', prompt: '水下城市, 透明穹顶, 海洋生物, 蓝绿色调, 气泡效果, 珊瑚装饰, 水流动态, 未来科技', style: 'underwater-city', category: '风景与场景' },
          { id: '54', name: '魔法森林', prompt: '魔法森林, 发光植物, 巨大蘑菇, 精灵生物, 迷雾环绕, 神秘光线, 奇幻色彩, 童话氛围', style: 'magic-forest', category: '风景与场景' },
          { id: '55', name: '极光夜空', prompt: '北极极光, 星空璀璨, 雪山剪影, 冰湖倒影, 绿色光带, 紫色天空, 宁静夜晚, 壮观自然现象', style: 'aurora-night', category: '风景与场景' },
          { id: '56', name: '蒸汽朋克世界', prompt: '蒸汽朋克世界, 铜制机械, 齿轮结构, 蒸汽管道, 维多利亚风格, 飞艇, 复古科技, 工业革命', style: 'steampunk-world', category: '风景与场景' },
          { id: '57', name: '热带海滩', prompt: '热带海滩, 白沙滩, 碧蓝海水, 棕榈树, 阳光明媚, 热带饮品, 度假氛围, 放松休闲', style: 'tropical-beach', category: '风景与场景' },
          { id: '58', name: '中国古代山水', prompt: '中国古代山水, 云雾缭绕, 陡峭山峰, 松树, 小桥流水, 亭台楼阁, 水墨风格, 诗意画面', style: 'chinese-landscape', category: '风景与场景' },
          { id: '59', name: '宇宙星云', prompt: '宇宙星云, 星团, 彩色气体, 恒星诞生, 深空背景, 宇宙尘埃, 壮观天文现象, 浩瀚宇宙', style: 'cosmic-nebula', category: '风景与场景' },
          { id: '60', name: '微观世界', prompt: '微观世界, 细胞结构, 分子排列, 有机纹理, 生物发光, 科学之美, 抽象形态, 微观细节', style: 'microscopic', category: '风景与场景' },
          
          // 新增10组高质量提示词
          { id: '61', name: '水墨山水画', prompt: '传统中国水墨画, 高山流水, 松柏远景, 云海缭绕, 空灵意境, 笔触精妙, 墨色深浅, 意境悠然', style: 'chinese-ink', category: '艺术与特殊效果' },
          { id: '62', name: '水晶宫殿', prompt: '水晶宫殿, 透明建筑, 反光光线, 水晶柱子, 彩色玻璃, 广阔大厅, 璀璨灯光, 梦幻氛围', style: 'crystal-palace', category: '风景与场景' },
          { id: '63', name: '机械未来主义', prompt: '机械未来主义, 金属结构, 齿轮运作, 蒸汽动力, 复古科技, 青铜质感, 机械手表, 工业美学', style: 'mechanical-futurism', category: '艺术与特殊效果' },
          { id: '64', name: '生物发光', prompt: '生物发光现象, 荧光植物, 发光海洋生物, 蓝色光波, 夜光森林, 神秘氛围, 自然奇观, 暗夜光辉', style: 'bioluminescence', category: '自然与科学' },
          { id: '65', name: '精灵王国', prompt: '精灵王国, 微型建筑, 蘑菇房屋, 发光植物, 小精灵, 蝴蝶翼, 森林小径, 童话氛围', style: 'fairy-kingdom', category: '奇幻世界' },
          { id: '66', name: '青铜时代', prompt: '青铜时代文明, 古代战士, 青铜盔甲, 工艺精美, 古代神庙, 历史场景, 神话氛围, 古代文明', style: 'bronze-age', category: '历史与文化' },
          { id: '67', name: '玫瑰花园', prompt: '玫瑰花园, 盛开玫瑰, 花香回荡, 花架间道, 花园雨后, 晨光露珠, 浮零花瓣, 浪漫氛围', style: 'rose-garden', category: '自然与植物' },
          { id: '68', name: '治愈系森林', prompt: '治愈系森林, 阳光透射, 绿意盛行, 清新空气, 湖水倍影, 青苔地毯, 小溪流水, 安逸平静', style: 'healing-forest', category: '自然与植物' },
          { id: '69', name: '宇宙飞船', prompt: '未来宇宙飞船, 星际巡航, 先进科技, 太空视角, 宇航员, 控制面板, 星空背景, 太空旅行', style: 'spaceship', category: '科幻与未来' },
          { id: '70', name: '深海探索', prompt: '深海探索, 海底地貌, 深海生物, 深海漆黑, 水压效应, 神秘海沟, 海底灯光, 海底探索船', style: 'deep-sea', category: '自然与科学' },
          
          // 用户提供的特色提示词
          { id: '71', name: '小红龙工作室', prompt: '一只小小的红龙蕱缩在中世纪巫师的木桌上, 微距镜头, 柔焦效果, 清晨柔和光线, 淡雅色彩, 奇幻蒸汽, 宁静氛围, 中世纪魔法师工作室', style: 'tiny-dragon', category: '奇幻世界' },
          { id: '72', name: '演讲者特写', prompt: '一位有魅力的演讲者, 棉色短发, 圆脸, 黑色边框眼镜, 手持黑色麦克风, 激情演讲, 生动表情, 浅蓝色毛衣, 白色T恤, 专业会议场景', style: 'speaker-portrait', category: '人物与肖像' },
          { id: '73', name: '悬崖小屋', prompt: '被洪水侵蚀的山巅小屋, 饱经风霜的木屋, 悬崖边缘, 低角度视角, 小阳台, 晒晒衣物, 正午烈日, 苏翠绿植, 阴森氛围, 高对比度', style: 'cliff-house', category: '风景与场景' },
          { id: '74', name: '女孩写实照', prompt: '一张黑发女子的自拍照, 20岁, 凌乱房间, 深度伪造海报, 柔和光线, 写实风格, 高清细节', style: 'realistic-portrait', category: '人物与肖像' },
          { id: '75', name: '前线士兵', prompt: '电影场景, 前线士兵的肩部特写, 眼神专注而迷人, 背景中可见爆炸, 胶片颗粒, 景深效果, 电影感画面', style: 'soldier-closeup', category: '人物与肖像' },
          { id: '76', name: '咖啡店工作', prompt: '一张写实照片, 一个女人在咖啡馆里用笔记本电脑打字, 身边放着一杯咖啡, 聚焦在女人的脸上, 自然光线, 生活场景', style: 'cafe-work', category: '人物与肖像' },
          { id: '77', name: '武装小女孩', prompt: '电影场景, 一个年轻的金发女孩穿着脆脏的浅绿色连衣裙, 拿着一把巨大的现代猎枪, 背景是扑来的橙色火焰和爆炸, 戏剧性对比', style: 'armed-girl', category: '人物与肖像' },
          { id: '78', name: '智能手机展示', prompt: '智能手机产品展示, 手机显示沉浸式壁纸和时间, 模糊的绿松石背景, 产品摄影, 高清细节, 商业风格', style: 'smartphone-showcase', category: '产品与科技' },
          { id: '79', name: '埃菲尔铁塔开幕', prompt: '1889年巴黎埃菲尔铁塔开幕照片, 高耳铁结构, 建设中, 巴黎天际线, 人群聚集, 19世纪晚期服饰, 历史性时刻, 建筑壮举, 复古色调', style: 'eiffel-tower-opening', category: '历史与文化' },
          { id: '80', name: '失落之城电影海报', prompt: '失落之城的回声 - 电影海报, 坐韧探险者, 磨损的皮夹克, 手持地图, 古老丛林边缘, 藤蔓覆盖的失落城市, 金色光芒, 阴影遮面, 黑暗情绪光线, 土色调, 发现埋藏在大地深处的秘密', style: 'lost-city-poster', category: '艺术与特殊效果' },
        ];
        setPresets(randomPrompts);
      } catch (error) {
        console.error('加载模型失败:', error);
        console.error('无法加载AI模型，请检查网络连接');
        
        // 即使加载模型失败，也要确保随机提示词库已初始化
        // 使用默认的随机提示词库，与 generateRandomPrompt 函数中的默认提示词保持一致
        const defaultRandomPrompts = [
          // 基础模型提示词
          { id: '1', name: '梦幻风景', prompt: '梦幻般的风景, 高山, 湖泊, 瀑布, 晚霞, 高清细节, 艺术感, 4K超高清', style: 'landscape', category: '核心基础模型' },
          { id: '2', name: '科幻城市', prompt: '未来科幻城市, 霓虹灯, 高楼大厦, 飞行器, 科技感, 细节丰富, 夜景, 光影效果', style: 'scifi', category: '核心基础模型' },
          { id: '3', name: '写实人像', prompt: '专业人像摄影, 自然光线, 浅景深, 精致五官, 完美肤质, 时尚服装, 工作室布景', style: 'portrait', category: '核心基础模型' },
          { id: '4', name: '自然风光', prompt: '壮丽自然风光, 日出, 山脉, 森林, 河流, 云海, 鸟瞰视角, 广角镜头, 高动态范围', style: 'nature', category: '核心基础模型' },
          { id: '5', name: '静物艺术', prompt: '精美静物摄影, 水果, 花卉, 玻璃器皿, 柔和光线, 质感表现, 专业布光, 高对比度', style: 'still-life', category: '核心基础模型' },
          
          // 新增提示词
          { id: '51', name: '霓虹赛博朋克', prompt: '霓虹赛博朋克城市, 雨夜, 反光地面, 高科技广告牌, 悬浮汽车, 全息投影, 拥挤街道, 未来感建筑', style: 'cyberpunk-neon', category: '风景与场景' },
          { id: '52', name: '古代神庙', prompt: '古代神庙遗迹, 巨大石柱, 神秘雕刻, 晨雾缭绕, 阳光穿透, 古老文明, 宏伟建筑, 历史感', style: 'ancient-temple', category: '风景与场景' },
          { id: '53', name: '水下城市', prompt: '水下城市, 透明穹顶, 海洋生物, 蓝绿色调, 气泡效果, 珊瑚装饰, 水流动态, 未来科技', style: 'underwater-city', category: '风景与场景' },
          { id: '54', name: '魔法森林', prompt: '魔法森林, 发光植物, 巨大蘑菇, 精灵生物, 迷雾环绕, 神秘光线, 奇幻色彩, 童话氛围', style: 'magic-forest', category: '风景与场景' },
          { id: '55', name: '极光夜空', prompt: '北极极光, 星空璀璨, 雪山剪影, 冰湖倒影, 绿色光带, 紫色天空, 宁静夜晚, 壮观自然现象', style: 'aurora-night', category: '风景与场景' }
        ];
        setPresets(defaultRandomPrompts);
      }
    };

    loadModels();
    
    // 初始化风格样式数据
    initializeStyles();
    
    // 加载提示词历史记录
    const savedHistory = localStorage.getItem('promptHistory');
    if (savedHistory) {
      try {
        const parsedHistory = JSON.parse(savedHistory);
        setPromptHistory(parsedHistory);
      } catch (error) {
        console.error('加载提示词历史记录失败:', error);
      }
    }
    
    // 加载标签数据
    const savedTags = localStorage.getItem('imageTags');
    if (savedTags) {
      try {
        const parsedTags = JSON.parse(savedTags);
        setTags(parsedTags);
      } catch (error) {
        console.error('加载标签数据失败:', error);
      }
    }
    
    // 加载收藏夹数据
    const savedFolders = localStorage.getItem('imageFolders');
    if (savedFolders) {
      try {
        const parsedFolders = JSON.parse(savedFolders);
        setFolders(parsedFolders);
      } catch (error) {
        console.error('加载收藏夹数据失败:', error);
      }
    }
  }, []);

  // 保存标签数据到本地存储
  useEffect(() => {
    if (tags.length > 0) {
      localStorage.setItem('imageTags', JSON.stringify(tags));
    }
  }, [tags]);
  
  // 保存收藏夹数据到本地存储
  useEffect(() => {
    if (folders.length > 0) {
      localStorage.setItem('imageFolders', JSON.stringify(folders));
    }
  }, [folders]);
  
  // 保存图片数据到本地存储
  useEffect(() => {
    if (galleryImages.length > 0) {
      localStorage.setItem('generatedImages', JSON.stringify(galleryImages));
    }
  }, [galleryImages]);

  // 添加标签相关函数
  const createTag = (name: string, color: string = '#3B82F6') => {
    const newTag: Tag = {
      id: `tag-${Date.now()}`,
      name,
      color,
      count: 0
    };
    
    setTags(prevTags => [...prevTags, newTag]);
    return newTag.id;
  };
  
  const deleteTag = (tagId: string) => {
    // 从所有图片中移除该标签
    const updatedImages = galleryImages.map(img => {
      if (img.tags && img.tags.includes(tagId)) {
        return {
          ...img,
          tags: img.tags.filter(id => id !== tagId)
        };
      }
      return img;
    });
    
    setGalleryImages(updatedImages);
    setTags(prevTags => prevTags.filter(tag => tag.id !== tagId));
  };
  
  const addTagToImage = (imageId: string, tagId: string) => {
    const updatedImages = galleryImages.map(img => {
      if (img.id === imageId) {
        const currentTags = img.tags || [];
        if (!currentTags.includes(tagId)) {
          // 更新标签计数
          setTags(prevTags => 
            prevTags.map(tag => 
              tag.id === tagId ? { ...tag, count: tag.count + 1 } : tag
            )
          );
          
          return {
            ...img,
            tags: [...currentTags, tagId]
          };
        }
      }
      return img;
    });
    
    setGalleryImages(updatedImages);
  };
  
  const removeTagFromImage = (imageId: string, tagId: string) => {
    const updatedImages = galleryImages.map(img => {
      if (img.id === imageId && img.tags && img.tags.includes(tagId)) {
        // 更新标签计数
        setTags(prevTags => 
          prevTags.map(tag => 
            tag.id === tagId ? { ...tag, count: Math.max(0, tag.count - 1) } : tag
          )
        );
        
        return {
          ...img,
          tags: img.tags.filter(id => id !== tagId)
        };
      }
      return img;
    });
    
    setGalleryImages(updatedImages);
  };
  
  // 收藏夹相关函数
  const createFolder = (name: string, description: string = '') => {
    const newFolder: ImageFolder = {
      id: `folder-${Date.now()}`,
      name,
      description,
      imageIds: [],
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    };
    
    setFolders(prevFolders => [...prevFolders, newFolder]);
    return newFolder.id;
  };
  
  const deleteFolder = (folderId: string) => {
    // 从文件夹中移除图片关联，但不删除图片
    const updatedImages = galleryImages.map(img => {
      if (img.folder === folderId) {
        return {
          ...img,
          folder: undefined
        };
      }
      return img;
    });
    
    setGalleryImages(updatedImages);
    setFolders(prevFolders => prevFolders.filter(folder => folder.id !== folderId));
    
    // 如果当前选中的就是被删除的文件夹，清除选择
    if (selectedFolder === folderId) {
      setSelectedFolder(null);
    }
  };
  
  const addImageToFolder = (imageId: string, folderId: string) => {
    // 更新图片的文件夹关联
    const updatedImages = galleryImages.map(img => 
      img.id === imageId ? { ...img, folder: folderId } : img
    );
    
    setGalleryImages(updatedImages);
    
    // 更新文件夹的图片列表
    setFolders(prevFolders => 
      prevFolders.map(folder => {
        if (folder.id === folderId && !folder.imageIds.includes(imageId)) {
          return {
            ...folder,
            imageIds: [...folder.imageIds, imageId],
            updatedAt: new Date().toISOString()
          };
        }
        return folder;
      })
    );
  };
  
  const removeImageFromFolder = (imageId: string, folderId: string) => {
    // 更新图片的文件夹关联
    const updatedImages = galleryImages.map(img => 
      img.id === imageId && img.folder === folderId ? { ...img, folder: undefined } : img
    );
    
    setGalleryImages(updatedImages);
    
    // 更新文件夹的图片列表
    setFolders(prevFolders => 
      prevFolders.map(folder => {
        if (folder.id === folderId) {
          return {
            ...folder,
            imageIds: folder.imageIds.filter(id => id !== imageId),
            updatedAt: new Date().toISOString()
          };
        }
        return folder;
      })
    );
  };
  
  // 收藏/取消收藏图片
  const toggleFavorite = (imageId: string) => {
    const updatedImages = galleryImages.map(img => 
      img.id === imageId ? { ...img, isFavorite: !img.isFavorite } : img
    );
    
    setGalleryImages(updatedImages);
  };

  // 生成图片
  const handleGenerate = async () => {
    if (!selectedModel || !prompt.trim()) return;
    
    setIsGenerating(true);
    
    try {
      // 调用API生成图片
      const images = await generateImage({
        model: selectedModel.id,
        prompt,
        negative_prompt: negativePrompt,
        aspect_ratio: useCustomSize ? undefined : aspectRatio,
        quality,
        seed,
        count,
        style: selectedStyle,
        resolution: useCustomSize ? `${customWidth}x${customHeight}` : resolution,
        customWidth: useCustomSize ? customWidth : undefined,
        customHeight: useCustomSize ? customHeight : undefined
      });
      
      // 更新生成的图片
      setPreviewImages(images);
      
      // 添加到画廊
      setGalleryImages(prev => [...images, ...prev]);
      
      // 添加到提示词历史记录
      addToPromptHistory(prompt, negativePrompt);
      
      // 成功提示
      toast.success('图片生成成功！');
    } catch (error) {
      console.error('生成图片失败:', error);
      
      // 错误处理
      if (error instanceof Error) {
        if (error.message === 'NETWORK_UNAVAILABLE') {
          toast.error('网络连接不可用，请检查您的网络连接');
        } else if (error.message === 'API_UNAVAILABLE') {
          toast.error('API服务不可用，请稍后再试');
        } else if (error.message === 'IMAGE_LOAD_FAILED') {
          toast.error('图片加载失败，请尝试使用不同的提示词');
        } else if (error.message === 'API_REQUEST_FAILED') {
          toast.error('API请求失败，请稍后再试');
        } else {
          toast.error(`生成失败: ${error.message}`);
        }
      } else {
        toast.error('生成图片时发生未知错误');
      }
    } finally {
      setIsGenerating(false);
    }
  };

  // 此函数保留但不再使用，因为预设提示词功能已移除
  const usePreset = (preset: PromptPreset) => {
    setPrompt(preset.prompt);
    // 不显示任何提示
  };

  // 随机提示词
  const generateRandomPrompt = () => {
    if (presets.length === 0) {
      // 如果presets数组为空，使用默认提示词
      const defaultPrompts = [
        // 基础提示词
        '梦幻般的风景, 高山, 湖泊, 瀑布, 晚霞, 高清细节, 艺术感, 4K超高清',
        '未来科幻城市, 霓虹灯, 高楼大厦, 飞行器, 科技感, 细节丰富, 夜景, 光影效果',
        '专业人像摄影, 自然光线, 浅景深, 精致五官, 完美肤质, 时尚服装, 工作室布景',
        '壮丽自然风光, 日出, 山脉, 森林, 河流, 云海, 鸟瞰视角, 广角镜头, 高动态范围',
        '精美静物摄影, 水果, 花卉, 玻璃器皿, 柔和光线, 质感表现, 专业布光, 高对比度',
        
        // 新增提示词
        '霓虹赛博朋克城市, 雨夜, 反光地面, 高科技广告牌, 悬浮汽车, 全息投影, 拥挤街道, 未来感建筑',
        '古代神庙遗迹, 巨大石柱, 神秘雕刻, 晨雾缭绕, 阳光穿透, 古老文明, 宏伟建筑, 历史感',
        '水下城市, 透明穹顶, 海洋生物, 蓝绿色调, 气泡效果, 珊瑚装饰, 水流动态, 未来科技',
        '魔法森林, 发光植物, 巨大蘑菇, 精灵生物, 迷雾环绕, 神秘光线, 奇幻色彩, 童话氛围',
        '北极极光, 星空璀璨, 雪山剪影, 冰湖倒影, 绿色光带, 紫色天空, 宁静夜晚, 壮观自然现象',
        '蒸汽朋克世界, 铜制机械, 齿轮结构, 蒸汽管道, 维多利亚风格, 飞艇, 复古科技, 工业革命',
        '热带海滩, 白沙滩, 碧蓝海水, 棕榈树, 阳光明媚, 热带饮品, 度假氛围, 放松休闲',
        '中国古代山水, 云雾缭绕, 陡峭山峰, 松树, 小桥流水, 亭台楼阁, 水墨风格, 诗意画面',
        '宇宙星云, 星团, 彩色气体, 恒星诞生, 深空背景, 宇宙尘埃, 壮观天文现象, 浩瀚宇宙',
        '微观世界, 细胞结构, 分子排列, 有机纹理, 生物发光, 科学之美, 抽象形态, 微观细节',
        
        // 新增10组高质量提示词
        '传统中国水墨画, 高山流水, 松柏远景, 云海缭绕, 空灵意境, 笔触精妙, 墨色深浅, 意境悠然',
        '水晶宫殿, 透明建筑, 反光光线, 水晶柱子, 彩色玻璃, 广阔大厅, 璀璨灯光, 梦幻氛围',
        '机械未来主义, 金属结构, 齿轮运作, 蒸汽动力, 复古科技, 青铜质感, 机械手表, 工业美学',
        '生物发光现象, 荧光植物, 发光海洋生物, 蓝色光波, 夜光森林, 神秘氛围, 自然奇观, 暗夜光辉',
        '精灵王国, 微型建筑, 蘑菇房屋, 发光植物, 小精灵, 蝴蝶翼, 森林小径, 童话氛围',
        '青铜时代文明, 古代战士, 青铜盔甲, 工艺精美, 古代神庙, 历史场景, 神话氛围, 古代文明',
        '玫瑰花园, 盛开玫瑰, 花香回荡, 花架间道, 花园雨后, 晨光露珠, 浮零花瓣, 浪漫氛围',
        '治愈系森林, 阳光透射, 绿意盛行, 清新空气, 湖水倍影, 青苔地毯, 小溪流水, 安逸平静',
        '未来宇宙飞船, 星际巡航, 先进科技, 太空视角, 宇航员, 控制面板, 星空背景, 太空旅行',
        '深海探索, 海底地貌, 深海生物, 深海漆黑, 水压效应, 神秘海沟, 海底灯光, 海底探索船',
        
        // 用户提供的特色提示词
        '一只小小的红龙蜷缩在中世纪巫师的桌子上, 微距镜头(f/2.8, 50mm), 佳能EOS R5, 柔焦效果, 清晨柔和光线, 淡雅色彩, 奇幻蒸汽, 宁静氛围, 16K和8K分辨率, 精细细节, 中世纪魅力',
        '一位有魅力的演讲者, 棕色短发微微凌乱, 圆脸, 刮过胡子, 黑色边框圆形眼镜, 右手持黑色麦克风, 激情演讲, 左手做手势, 表情生动, 浅蓝色套头衫, 白色T恤, 黑色挂绳, AI/MLAPI徽章, 专业会议场景',
        '被洪水侵蚀的山巅小屋, 从低角度展示一座饱经风霜的木屋, 摇摇欲坠地矗立在陡峭的悬崖边缘, 小阳台晾晒衣物, 正午烈日下锐利阴影, 苍翠绿植环绕悬崖底部, 茂密植被遮掩广阔风景, 阴森孤立氛围, 锐利高对比度细节, 荒凉孤独感',
        '一张20岁黑发女子的自拍照, 她身处一间凌乱的房间中, 墙上挂着一张写着"深度伪造"(DEEPFAKE)的海报, 光线柔和并有方向性, 写实风格, 高清细节',
        '电影场景, 前线士兵的肩部特写近景, 眼神专注而迷人, 背景中可见爆炸, 有胶片颗粒和景深效果, 电影感画面, 战争场景, 情感表达, 高对比度',
        '一张原始的写实照片, 一个女人在咖啡馆里用笔记本电脑打字, 她身边放着一杯咖啡, 聚焦在女人的脸上, 自然光线, 生活场景, 日常瞬间, 真实表达',
        '电影场景, 一个年轻的金发女孩穿着脏脏的浅绿色连衣裙, 拿着一把巨大的现代猎枪, 背景是戏剧性的橙色火焰和爆炸, 强烈对比, 紧张氛围, 电影质感',
        '一个智能手机产品展示, 智能手机显示沉浸式壁纸和时间, 模糊的绿松石背景, 产品摄影, 高清细节, 商业风格, 现代感, 科技美学',
        '1889年巴黎埃菲尔铁塔开幕时的照片, 高耸的铁结构仍在建设中, 背景是熙熙攘攘的巴黎天际线, 人群穿着19世纪晚期的服装聚集在周围, 惊叹于这座标志性的新地标, 历史性时刻, 建筑壮举, 复古色调, 宏大历史氛围',
        '"失落之城的回声" - 电影海报, 一位坚韧的探险者, 穿着磨损的皮夹克, 手中拿着地图, 站在古老丛林的边缘, 人物脸部被部分阴影遮掩增添神秘感, 背景是被藤蔓覆盖的失落之城, 透过迷雾隐约可见, 废墟深处散发着微弱的金色光芒, 黑暗、富有情绪的光线, 写实且电影感十足, 柔和的土色调和质感, 暗示时间的流逝与衰败'
      ];
      const randomIndex = Math.floor(Math.random() * defaultPrompts.length);
      setPrompt(defaultPrompts[randomIndex]);
      toast.info('已生成随机提示词');
    } else {
      const randomIndex = Math.floor(Math.random() * presets.length);
      setPrompt(presets[randomIndex].prompt);
      toast.info('已生成随机提示词');
    }
  };

  // 删除单张图片
  const removeImage = (imageId: string) => {
    setGalleryImages(prev => prev.filter(img => img.id !== imageId));
  };
  
  // 清除画廊
  const clearGallery = () => {
    if (window.confirm('确定要清空画廊吗？此操作不可撤销。')) {
      setGalleryImages([]);
      // 不显示提示
    }
  };

  // 添加提示词到历史记录
  const addToPromptHistory = (promptText: string, negativePromptText: string) => {
    if (!promptText.trim() || !selectedModel) return;
    
    const now = new Date();
    const newHistoryItem: PromptHistory = {
      id: `history-${Date.now()}`,
      prompt: promptText,
      negative_prompt: negativePromptText || undefined,
      model: selectedModel.id,
      created_at: now.toISOString(),
      used_count: 1
    };
    
    // 检查是否已存在相同的提示词
    const existingIndex = promptHistory.findIndex(
      item => item.prompt === promptText && item.negative_prompt === negativePromptText
    );
    
    if (existingIndex >= 0) {
      // 更新已存在的提示词使用次数和时间
      const updatedHistory = [...promptHistory];
      updatedHistory[existingIndex] = {
        ...updatedHistory[existingIndex],
        used_count: updatedHistory[existingIndex].used_count + 1,
        created_at: now.toISOString()
      };
      setPromptHistory(updatedHistory);
    } else {
      // 添加新的提示词到历史记录，限制最多保存20条
      setPromptHistory(prev => [newHistoryItem, ...prev].slice(0, 20));
    }
  };
  
  // 使用历史提示词
  const useHistoryPrompt = (historyItem: PromptHistory) => {
    setPrompt(historyItem.prompt);
    if (historyItem.negative_prompt) {
      setNegativePrompt(historyItem.negative_prompt);
    }
    
    // 更新使用次数
    const updatedHistory = promptHistory.map(item => 
      item.id === historyItem.id 
        ? { ...item, used_count: item.used_count + 1, created_at: new Date().toISOString() }
        : item
    );
    
    // 按最近使用时间重新排序
    updatedHistory.sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
    
    setPromptHistory(updatedHistory);
  };

  // 添加数据备份和恢复功能
  const exportData = () => {
    try {
      // 准备导出的数据
      const dataToExport = {
        version: '1.0',
        timestamp: new Date().toISOString(),
        galleryImages,
        tags,
        folders,
        promptHistory
      };
      
      // 转换为JSON字符串
      const jsonString = JSON.stringify(dataToExport, null, 2);
      
      // 创建Blob对象
      const blob = new Blob([jsonString], { type: 'application/json' });
      
      // 使用file-saver库下载文件
      saveAs(blob, `ai-painter-backup-${new Date().toISOString().split('T')[0]}.json`);
      
      toast.success('数据导出成功！');
    } catch (error) {
      console.error('导出数据失败:', error);
      toast.error('导出数据失败，请查看控制台获取详细信息。');
    }
  };
  
  const importData = (event: React.ChangeEvent<HTMLInputElement>) => {
    try {
      const file = event.target.files?.[0];
      if (!file) return;
      
      const reader = new FileReader();
      
      reader.onload = (e) => {
        try {
          const jsonData = JSON.parse(e.target?.result as string);
          
          // 验证数据格式
          if (!jsonData.version || !jsonData.timestamp) {
            throw new Error('无效的备份文件格式');
          }
          
          // 确认导入
          if (window.confirm(`确定要导入此备份文件吗？\n创建时间: ${new Date(jsonData.timestamp).toLocaleString()}\n这将覆盖当前的所有数据！`)) {
            // 导入数据
            if (jsonData.galleryImages) setGalleryImages(jsonData.galleryImages);
            if (jsonData.tags) setTags(jsonData.tags);
            if (jsonData.folders) setFolders(jsonData.folders);
            if (jsonData.promptHistory) setPromptHistory(jsonData.promptHistory);
            
            toast.success('数据导入成功！');
          }
        } catch (error) {
          console.error('解析导入文件失败:', error);
          toast.error('导入失败，文件格式无效。');
        }
      };
      
      reader.onerror = () => {
        toast.error('读取文件失败。');
      };
      
      reader.readAsText(file);
      
      // 重置文件输入，以便可以重新选择同一文件
      event.target.value = '';
    } catch (error) {
      console.error('导入数据失败:', error);
      toast.error('导入数据失败，请查看控制台获取详细信息。');
    }
  };

  // 添加处理风格样式的函数
  const handleStyleChange = (style: Style | null) => {
    setSelectedStyle(style);
  };

  return (
    <div className="flex flex-col min-h-screen">
      {/* 移除右上角提示内容 */}
      <Header activeTab={activeTab} setActiveTab={setActiveTab} />
      
      <main className="flex-grow flex flex-col md:flex-row overflow-hidden">
        {activeTab === 'create' ? (
          <div className="flex flex-col md:flex-row h-full w-full">
            <Sidebar 
              models={models}
              selectedModel={selectedModel}
              setSelectedModel={setSelectedModel}
              prompt={prompt}
              setPrompt={setPrompt}
              negativePrompt={negativePrompt}
              setNegativePrompt={setNegativePrompt}
              aspectRatio={aspectRatio}
              setAspectRatio={setAspectRatio}
              quality={quality}
              setQuality={setQuality}
              seed={seed}
              setSeed={setSeed}
              count={count}
              setCount={setCount}
              onGenerate={handleGenerate}
              isGenerating={isGenerating}
              presets={presets}
              usePreset={usePreset}
              generateRandomPrompt={generateRandomPrompt}
              promptHistory={promptHistory}
              useHistoryPrompt={useHistoryPrompt}
              styleCategories={styleCategories}
              selectedStyle={selectedStyle}
              setSelectedStyle={handleStyleChange}
              resolution={resolution}
              setResolution={setResolution}
              useCustomSize={useCustomSize}
              setUseCustomSize={setUseCustomSize}
              customWidth={customWidth}
              setCustomWidth={setCustomWidth}
              customHeight={customHeight}
              setCustomHeight={setCustomHeight}
            />
            <div className="flex-1 flex justify-center px-6">
              <Canvas 
                prompt={prompt}
                selectedModel={selectedModel}
                isGenerating={isGenerating}
                aspectRatio={aspectRatio}
                previewImages={previewImages}
                count={count}
                selectedStyle={selectedStyle}
                resolution={resolution}
              />
            </div>
          </div>
        ) : (
          <>
            <Gallery 
              images={galleryImages}
              clearGallery={clearGallery}
              removeImage={removeImage}
              tags={tags}
              createTag={createTag}
              deleteTag={deleteTag}
              addTagToImage={addTagToImage}
              removeTagFromImage={removeTagFromImage}
              folders={folders}
              createFolder={createFolder}
              deleteFolder={deleteFolder}
              addImageToFolder={addImageToFolder}
              removeImageFromFolder={removeImageFromFolder}
              selectedTags={selectedTags}
              setSelectedTags={setSelectedTags}
              selectedFolder={selectedFolder}
              setSelectedFolder={setSelectedFolder}
              showFavorites={showFavorites}
              setShowFavorites={setShowFavorites}
              toggleFavorite={toggleFavorite}
              exportData={exportData}
              importData={importData}
            />
            
            {/* 隐藏的文件输入用于导入数据 */}
            <input 
              type="file" 
              id="import-data-input" 
              accept=".json" 
              onChange={importData} 
              style={{ display: 'none' }} 
            />
          </>
        )}
      </main>
      
      <Footer />
    </div>
  );
};

export default App;
